import { useState } from 'react'
import { usePlanner, plannerStats } from '../state/planner'
import { useTours } from '../state/tours'
import { useNav } from '../state/nav'
import { useUi } from '../state/ui'
import { SPORTS } from '../lib/models'
import type { Fitness } from '../lib/routing'
import { formatDuration, formatKm } from '../lib/geo'
import { toGpx } from '../lib/gpx'
import { encodePlanUrl } from '../lib/share'
import ElevationChart from './ElevationChart'
import SurfaceBars from './SurfaceBars'
import SearchBar from './SearchBar'
import { downloadText } from './util'
import { SPORT_ICONS } from '../App'
import { friendlyGeoError } from '../lib/platform'
import {
  IconUndo,
  IconSwap,
  IconLoop,
  IconClose,
  IconNavArrow,
  IconGpx,
  IconShare,
  IconCheck,
  IconLocate,
} from './icons'

export default function PlannerPanel() {
  const p = usePlanner()
  const stats = plannerStats(p)
  const showToast = useUi((s) => s.showToast)
  const [roundKm, setRoundKm] = useState(15)
  const [saveOpen, setSaveOpen] = useState(false)
  const [name, setName] = useState('')
  const [locating, setLocating] = useState(false)

  const startAtMyLocation = () => {
    setLocating(true)
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocating(false)
        usePlanner.getState().addWaypoint([pos.coords.longitude, pos.coords.latitude], 'My location')
      },
      (e) => {
        setLocating(false)
        showToast(friendlyGeoError(e.code))
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 30000 },
    )
  }

  const save = async () => {
    if (!p.route || !stats) return
    const tourName = name.trim() || `${SPORTS.find((s) => s.id === p.sport)?.label} tour`
    await useTours.getState().save({
      id: crypto.randomUUID(),
      name: tourName,
      sport: p.sport,
      kind: 'planned',
      data: {
        coords: p.route.coords,
        waypoints: p.waypoints,
        instructions: p.route.instructions,
        surfaces: p.extras?.surfaces,
        waytypes: p.extras?.waytypes,
        stats: {
          distanceM: stats.distanceM,
          ascentM: stats.ascentM,
          descentM: stats.descentM,
          durationS: stats.durationS,
          minEleM: p.profile?.minEleM,
          maxEleM: p.profile?.maxEleM,
        },
      },
      createdAt: Date.now(),
      updatedAt: Date.now(),
    })
    setSaveOpen(false)
    setName('')
    showToast(`Saved “${tourName}”`)
  }

  const exportGpx = () => {
    if (!p.route) return
    downloadText(
      toGpx({
        name: name.trim() || 'Traverse route',
        coords: p.route.coords,
        waypoints: p.waypoints.map((w, i) => ({ name: w.name ?? `Waypoint ${i + 1}`, lng: w.lng, lat: w.lat })),
      }),
      'traverse-route.gpx',
      'application/gpx+xml',
    )
  }

  const share = async () => {
    const url = encodePlanUrl(
      p.waypoints.map((w) => [w.lng, w.lat]),
      p.sport,
    )
    try {
      if (navigator.share) await navigator.share({ title: 'Traverse route', url })
      else {
        await navigator.clipboard.writeText(url)
        showToast('Route link copied to clipboard')
      }
    } catch {
      /* user cancelled */
    }
  }

  const navigate = () => {
    if (!p.route) return
    useNav.getState().start('Planned route', p.route.coords, p.route.instructions, p.sport)
    useUi.getState().setSheetOpen(false)
  }

  return (
    <>
      <SearchBar
        placeholder="Search places…"
        onPick={(place) => usePlanner.getState().addWaypoint([place.lng, place.lat], place.name)}
      />

      {p.waypoints.length === 0 && (
        <button className="btn block" style={{ marginBottom: 12 }} onClick={startAtMyLocation} disabled={locating}>
          {locating ? <span className="busy-dot" /> : <IconLocate />}
          {locating ? 'Finding you…' : 'Start at my location'}
        </button>
      )}

      <div className="sports">
        {SPORTS.map((s) => {
          const Icon = SPORT_ICONS[s.id]
          return (
            <button key={s.id} className={p.sport === s.id ? 'active' : ''} onClick={() => p.setSport(s.id)}>
              <Icon />
              {s.label}
            </button>
          )
        })}
      </div>

      <div className="row" style={{ marginBottom: 12 }}>
        <span className="muted" style={{ flexShrink: 0 }}>
          Fitness
        </span>
        <div className="seg" style={{ flex: 1 }}>
          {(['casual', 'average', 'athletic'] as Fitness[]).map((f) => (
            <button key={f} className={p.fitness === f ? 'active' : ''} onClick={() => p.setFitness(f)}>
              {f}
            </button>
          ))}
        </div>
      </div>

      {p.waypoints.length > 0 && (
        <div className="wp-list">
          {p.waypoints.map((w, i) => (
            <div
              className={`wp-row${i === 0 ? ' first' : ''}${i === p.waypoints.length - 1 && i > 0 ? ' last' : ''}`}
              key={`${i}-${w.lng.toFixed(5)}-${w.lat.toFixed(5)}`}
            >
              <span className="wp-dot">{i + 1}</span>
              <span className="wp-name">{w.name ?? `${w.lat.toFixed(4)}, ${w.lng.toFixed(4)}`}</span>
              <button className="wp-x" onClick={() => p.removeWaypoint(i)} aria-label="Remove waypoint">
                <IconClose />
              </button>
            </div>
          ))}
        </div>
      )}

      {p.waypoints.length > 0 && (
        <div className="quick-row">
          <button className="chip" onClick={p.undo} disabled={!p.undoStack.length}>
            <IconUndo /> Undo
          </button>
          <button className="chip" onClick={p.reverse} disabled={p.waypoints.length < 2}>
            <IconSwap /> Reverse
          </button>
          <button className="chip" onClick={p.backToStart} disabled={p.waypoints.length < 2}>
            <IconLoop /> Close loop
          </button>
          <button className="chip danger" onClick={p.clear}>
            <IconClose /> Clear
          </button>
        </div>
      )}

      {p.waypoints.length === 1 && (
        <div className="shell">
          <div className="core">
            <div className="row" style={{ marginBottom: 6 }}>
              <span style={{ fontWeight: 800, fontSize: 13 }}>Round trip from here</span>
              <span className="spacer" />
              <span className="muted" style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>
                {roundKm} km
              </span>
            </div>
            <div className="row">
              <input
                type="range"
                min={5}
                max={80}
                value={roundKm}
                onChange={(e) => setRoundKm(Number(e.target.value))}
                style={{ flex: 1, accentColor: 'var(--accent)' }}
              />
              <button className="btn sm primary" onClick={() => void p.roundTrip(roundKm)}>
                <IconLoop /> Generate
              </button>
            </div>
          </div>
        </div>
      )}

      {p.busy && (
        <p className="muted" style={{ margin: '10px 2px' }}>
          <span className="busy-dot" /> Calculating route…
        </p>
      )}
      {p.error && <p className="error-text">{p.error}</p>}

      {stats && (
        <>
          <div className="shell">
            <div className="core">
              <div className="row" style={{ marginBottom: 12 }}>
                <span className={`difficulty ${stats.difficulty}`}>{stats.difficulty}</span>
                <span className="muted">
                  {SPORTS.find((s) => s.id === p.sport)?.label} · {p.fitness}
                </span>
              </div>
              <div className="stats">
                <div className="stat">
                  <div className="v">{formatKm(stats.distanceM)}</div>
                  <div className="l">Distance</div>
                </div>
                <div className="stat">
                  <div className="v">{formatDuration(stats.durationS)}</div>
                  <div className="l">Duration</div>
                </div>
                <div className="stat">
                  <div className="v">{stats.ascentM} m</div>
                  <div className="l">Ascent</div>
                </div>
                <div className="stat">
                  <div className="v">{stats.descentM} m</div>
                  <div className="l">Descent</div>
                </div>
              </div>
              {p.profile && <ElevationChart profile={p.profile} onHover={p.setHoverAtM} />}
            </div>
          </div>

          {p.extras && (
            <div className="shell">
              <div className="core">
                <SurfaceBars title="Way types" segments={p.extras.waytypes} />
                <SurfaceBars title="Surfaces" segments={p.extras.surfaces} />
              </div>
            </div>
          )}

          {saveOpen ? (
            <div style={{ margin: '12px 0' }}>
              <input
                className="field"
                placeholder="Give your tour a name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && void save()}
                autoFocus
              />
              <div className="row">
                <button className="btn primary" style={{ flex: 1 }} onClick={() => void save()}>
                  Save tour
                  <span className="isl">
                    <IconCheck />
                  </span>
                </button>
                <button className="btn" onClick={() => setSaveOpen(false)}>
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="row" style={{ margin: '12px 0 8px' }}>
                <button className="btn primary" style={{ flex: 1 }} onClick={() => setSaveOpen(true)}>
                  Save tour
                  <span className="isl">
                    <IconCheck />
                  </span>
                </button>
                <button className="btn dark" onClick={navigate}>
                  <IconNavArrow /> Navigate
                </button>
              </div>
              <div className="row">
                <button className="btn sm" style={{ flex: 1 }} onClick={exportGpx}>
                  <IconGpx /> GPX export
                </button>
                <button className="btn sm" style={{ flex: 1 }} onClick={() => void share()}>
                  <IconShare /> Share link
                </button>
              </div>
            </>
          )}
        </>
      )}
    </>
  )
}
